/**
 * Phase 69 Plan 03 — typed API client for the treatment lifecycle endpoints.
 *
 * Mirrors backend/app/Http/Controllers/Api/V1/Treatments/* 1:1.
 */
import api from "./api";
import type {
  ApiResponse,
  ClientTreatment,
  EligibleQuoteForTreatments,
  StagePhoto,
  TreatmentStatus,
} from "@/types";

export interface SpawnFromQuoteItemPayload {
  quote_item_id: string;
  teeth: number[];
  enabled_optional_stages?: string[];
}

export interface SpawnFromQuotePayload {
  quote_id: string;
  /** Omit to start EVERY remaining quote line in one click (teeth derived server-side). */
  items?: SpawnFromQuoteItemPayload[];
}

export interface SpawnAdhocPayload {
  catalog_item_id: string;
  teeth: number[];
  enabled_optional_stages?: string[];
  // Chart audit A1 (260905): the Planned path keeps the doctor's note (on the
  // first stage) and the follow-up request, like the quick path.
  note?: string | null;
  followup?: { target_date?: string | null } | null;
  // Phase 89 — who does this. Omit = the calling doctor (or nobody when the
  // desk starts it). Only sent where treatment_assignment_enabled.
  doctor_id?: number | null;
}

// Charting redesign Phase 2 — the "done today" fork: spawn + complete every
// enabled stage in one call, carrying the optional visit context.
export interface QuickTreatmentPayload {
  catalog_item_id: string;
  teeth: number[];
  enabled_optional_stages?: string[];
  note?: string | null;
  // Charting Workflow A (260814): backdatable clinical date + MODBL surfaces,
  // stamped onto the chart rows this treatment writes.
  performed_on?: string | null;
  surfaces?: string | null;
  appointment_id?: string | null;
  clinical_visit_id?: string | null;
  // Dentour 260813: "book a follow-up visit" marker → booking worklist.
  followup?: { target_date?: string | null } | null;
  // Phase 89 — see SpawnAdhocPayload.
  doctor_id?: number | null;
}

/** Server limit on treatment notes (QuickTreatmentRequest et al. max:5000). */
export const TREATMENT_NOTE_MAX = 5000;

export const listClientTreatments = async (
  clientId: string,
  filters?: { status?: TreatmentStatus[] },
): Promise<ClientTreatment[]> => {
  const params: Record<string, string> = {};
  if (filters?.status?.length) {
    params.status = filters.status.join(",");
  }
  const response = await api.get<ApiResponse<ClientTreatment[]>>(
    `/clients/${clientId}/treatments`,
    { params },
  );
  return response.data.data;
};

export const getTreatmentSheetPlan = async (clientId: string): Promise<{
  treatments: ClientTreatment[];
  startedQuoteItemIds: string[];
}> => {
  const response = await api.get<ApiResponse<ClientTreatment[]> & { started_quote_item_ids?: string[] }>(
    `/clients/${clientId}/treatments`,
  );
  return { treatments: response.data.data, startedQuoteItemIds: response.data.started_quote_item_ids ?? [] };
};

export const getEligibleQuoteForTreatments = async (
  clientId: string,
): Promise<EligibleQuoteForTreatments | null> => {
  const response = await api.get<ApiResponse<EligibleQuoteForTreatments | null>>(
    `/clients/${clientId}/treatments/eligible-quote`,
  );
  return response.data.data;
};

export const spawnTreatmentsFromQuote = async (
  clientId: string,
  payload: SpawnFromQuotePayload,
): Promise<ClientTreatment[]> => {
  const response = await api.post<ApiResponse<ClientTreatment[]>>(
    `/clients/${clientId}/treatments/spawn-from-quote`,
    payload,
  );
  return response.data.data;
};

export const spawnAdhocTreatment = async (
  clientId: string,
  payload: SpawnAdhocPayload,
): Promise<ClientTreatment> => {
  const response = await api.post<ApiResponse<ClientTreatment>>(
    `/clients/${clientId}/treatments/spawn-adhoc`,
    payload,
  );
  return response.data.data;
};

// Charting redesign Phase 2 — spawn + complete in one transaction ("done today").
export const quickTreatment = async (
  clientId: string,
  payload: QuickTreatmentPayload,
): Promise<ClientTreatment> => {
  const response = await api.post<ApiResponse<ClientTreatment>>(
    `/clients/${clientId}/treatments/quick`,
    payload,
  );
  return response.data.data;
};

/**
 * Phase 89 D-5 — PATCH /treatments/{id}/doctor. Null clears the assignment.
 * Needs treatments.assign; a narrowed doctor can only hand over a line they
 * can see.
 */
export const assignTreatmentDoctor = async (
  treatmentId: string,
  doctorId: number | null,
): Promise<ClientTreatment> => {
  const response = await api.patch<ApiResponse<ClientTreatment>>(
    `/treatments/${treatmentId}/doctor`,
    { doctor_id: doctorId },
  );
  return response.data.data;
};

/**
 * Phase 89 D-7 — GET /treatments/mine: the caller's planned + active
 * treatments across patients, soonest pending step first, each with a
 * `client` block.
 */
export const listMyTreatments = async (): Promise<ClientTreatment[]> => {
  const response = await api.get<ApiResponse<ClientTreatment[]>>("/treatments/mine");
  return response.data.data;
};

export const getTreatment = async (treatmentId: string): Promise<ClientTreatment> => {
  const response = await api.get<ApiResponse<ClientTreatment>>(`/treatments/${treatmentId}`);
  return response.data.data;
};

export const completeStage = async (
  treatmentId: string,
  stageId: string,
  body: { note?: string | null; actual_date?: string | null },
): Promise<ClientTreatment> => {
  const response = await api.post<ApiResponse<ClientTreatment>>(
    `/treatments/${treatmentId}/stages/${stageId}/complete`,
    body,
  );
  return response.data.data;
};

export const skipStage = async (
  treatmentId: string,
  stageId: string,
  body: { note?: string | null },
): Promise<ClientTreatment> => {
  const response = await api.post<ApiResponse<ClientTreatment>>(
    `/treatments/${treatmentId}/stages/${stageId}/skip`,
    body,
  );
  return response.data.data;
};

export const abandonTreatment = async (
  treatmentId: string,
  body: { reason?: string | null },
): Promise<ClientTreatment> => {
  const response = await api.post<ApiResponse<ClientTreatment>>(
    `/treatments/${treatmentId}/abandon`,
    body,
  );
  return response.data.data;
};

export const listStagePhotos = async (
  treatmentId: string,
  stageId: string,
): Promise<StagePhoto[]> => {
  const response = await api.get<ApiResponse<StagePhoto[]>>(
    `/treatments/${treatmentId}/stages/${stageId}/documents`,
  );
  return response.data.data;
};

export const uploadStagePhoto = async (
  treatmentId: string,
  stageId: string,
  file: File,
): Promise<StagePhoto> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post<ApiResponse<StagePhoto>>(
    `/treatments/${treatmentId}/stages/${stageId}/documents`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data.data;
};

/** Chart audit A6 (260905): re-bind a PLANNED treatment to different teeth. */
export const updateTreatmentTeeth = async (
  treatmentId: string,
  teeth: number[],
): Promise<ClientTreatment> => {
  const response = await api.patch<ApiResponse<ClientTreatment>>(
    `/treatments/${treatmentId}/teeth`,
    { teeth },
  );
  return response.data.data;
};
